import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Church, Check } from "lucide-react";
import { client } from "@/client";

interface RegistrationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RegistrationModal({ open, onOpenChange }: RegistrationModalProps) {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const res = await client.api.registrations.$post({
        json: formData,
      });
      if (res.ok) {
        setIsSubmitted(true);
      } else {
        console.error("Failed to submit registration");
      }
    } catch (error) {
      console.error("Error submitting registration:", error);
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    // Reset form after modal closes
    setTimeout(() => {
      setIsSubmitted(false);
      setFormData({ name: "", email: "", phone: "", date: "" });
    }, 300);
  };

  // Get next 4 Thursdays for date selection
  const getNextThursdays = () => {
    const thursdays = [];
    const today = new Date();
    const current = new Date(today);

    // Find next Thursday
    const dayOfWeek = current.getDay();
    const daysUntilThursday = (4 - dayOfWeek + 7) % 7;
    current.setDate(current.getDate() + (daysUntilThursday === 0 ? 7 : daysUntilThursday));

    for (let i = 0; i < 4; i++) {
      thursdays.push(new Date(current));
      current.setDate(current.getDate() + 7);
    }

    return thursdays;
  };

  const formatDate = (date: Date) => {
    const month = date.getMonth() + 1;
    const day = date.getDate();
    return `${month}월 ${day}일 (목)`;
  };

  const nextThursdays = getNextThursdays();

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px] bg-card border-border rounded-2xl rough-shadow">
        {!isSubmitted ? (
          <>
            <DialogHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="size-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground rough-shadow">
                  <Church className="size-5" />
                </div>
                <DialogTitle className="text-xl font-display font-bold rotate-slight">
                  방문 신청
                </DialogTitle>
              </div>
              <DialogDescription className="font-body">
                목요만찬에 참여를 원하시면 아래 정보를 입력해 주세요.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="font-body font-semibold">이름</Label>
                <Input
                  id="name"
                  placeholder="홍길동"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                  className="bg-secondary border-border rounded-lg font-body"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="font-body font-semibold">이메일</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@example.com"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  required
                  className="bg-secondary border-border rounded-lg font-body"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="font-body font-semibold">연락처</Label>
                <Input
                  id="phone"
                  type="tel"
                  placeholder="010-1234-5678"
                  value={formData.phone}
                  onChange={(e) =>
                    setFormData({ ...formData, phone: e.target.value })
                  }
                  required
                  className="bg-secondary border-border rounded-lg font-body"
                />
              </div>

              <div className="space-y-2">
                <Label className="font-body font-semibold">참여 희망 날짜</Label>
                <div className="grid grid-cols-2 gap-2">
                  {nextThursdays.map((date, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() =>
                        setFormData({
                          ...formData,
                          date: date.toISOString(),
                        })
                      }
                      className={`p-3 rounded-lg border text-sm font-body font-medium transition-all hover-lift ${
                        formData.date === date.toISOString()
                          ? "bg-primary text-primary-foreground border-primary rough-shadow"
                          : "bg-secondary border-border text-foreground hover:border-primary/50"
                      }`}
                    >
                      {formatDate(date)}
                    </button>
                  ))}
                </div>
              </div>

              <Button
                type="submit"
                className="w-full rounded-2xl font-body font-bold mt-6 rough-shadow hover-lift"
                disabled={!formData.name || !formData.email || !formData.phone || !formData.date}
              >
                신청하기
              </Button>
            </form>
          </>
        ) : (
          <div className="py-8 text-center">
            <div className="size-16 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-4 rough-shadow">
              <Check className="size-8 text-primary" />
            </div>
            <h3 className="text-xl font-display font-bold text-foreground mb-2 rotate-slight">
              신청이 완료되었습니다!
            </h3>
            <p className="text-muted-foreground mb-6 font-body">
              입력하신 이메일로 확인 메일을 보내드릴 예정입니다.
              <br />
              목요일에 뵙겠습니다!
            </p>
            <Button
              onClick={handleClose}
              className="rounded-2xl font-body font-bold rough-shadow hover-lift"
            >
              확인
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}